// utils/migrateOpeningBalances.js
import axios from "axios";
import { TransactionHooks } from "./transactionHooks";

/**
 * Migration Script: Create Opening Balance Journal Entries
 *
 * This script creates journal entries for existing account balances
 * to properly initialize the double-entry accounting system.
 *
 * Run this ONCE to set up opening balances.
 */

const API_BASE = "https://zedlenders.pickmesms.com/items";

export const migrateOpeningBalances = async ({ company, token }) => {
  try {
    console.log("=== Starting Opening Balance Migration ===");

    // 1. Fetch all accounts for the company
    const accountsResponse = await axios.get(`${API_BASE}/accounts`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        filter: { company: company.id },
        fields: "id,name,balance,status",
      },
    });

    const accounts = accountsResponse.data.data || [];
    const activeAccounts = accounts.filter(
      (acc) => acc.status === "active" || !acc.status
    );

    console.log(`Found ${activeAccounts.length} active accounts`);

    // 2. Calculate total opening balance
    const totalBalance = activeAccounts.reduce(
      (sum, acc) => sum + parseFloat(acc.balance || 0),
      0
    );

    console.log(`Total opening balance: ZMW ${totalBalance.toLocaleString()}`);

    if (totalBalance <= 0) {
      console.log("⚠️  No positive balance found. Skipping migration.");
      return {
        success: false,
        message: "No positive balance to migrate",
      };
    }

    // 3. Create opening balance journal entry for each account
    const results = {
      success: 0,
      failed: 0,
      total: activeAccounts.length,
      details: [],
    };

    for (const account of activeAccounts) {
      const balance = parseFloat(account.balance || 0);

      if (balance <= 0) {
        console.log(`Skipping ${account.name}: Zero or negative balance`);
        continue;
      }

      try {
        console.log(`Creating opening balance for ${account.name}: ZMW ${balance}`);

        await TransactionHooks.onFloatAdded({
          amount: balance,
          date: new Date("2024-01-01"), // Use your company's start date
          company: company.id,
          token,
          accountId: account.id,
          source: "Owner", // Change to "Investor" if needed
        });

        results.success++;
        results.details.push({
          account: account.name,
          amount: balance,
          status: "success",
        });

        console.log(`✓ Created opening balance for ${account.name}`);
      } catch (error) {
        console.error(`✗ Failed for ${account.name}:`, error.message);
        results.failed++;
        results.details.push({
          account: account.name,
          amount: balance,
          status: "failed",
          error: error.message,
        });
      }
    }

    console.log("\n=== Migration Complete ===");
    console.log(`Success: ${results.success}/${results.total}`);
    console.log(`Failed: ${results.failed}/${results.total}`);

    return {
      success: true,
      results,
      message: `Migrated ${results.success} accounts successfully`,
    };
  } catch (error) {
    console.error("Migration failed:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Check if opening balances have already been migrated
 */
export const checkOpeningBalances = async ({ company, token }) => {
  try {
    const response = await axios.get(`${API_BASE}/journal_entries`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        filter: {
          company: company.id,
          reference: { _starts_with: "FLOAT-" },
        },
        limit: 1,
      },
    });

    const hasEntries = (response.data.data || []).length > 0;

    return {
      migrated: hasEntries,
      message: hasEntries
        ? "Opening balances already exist"
        : "No opening balance entries found",
    };
  } catch (error) {
    console.error("Error checking opening balances:", error);
    return { migrated: false, error: error.message };
  }
};

export default { migrateOpeningBalances, checkOpeningBalances };
